import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { InterviewData, InterviewStatus } from '@/api/interviewApi'
import { InterviewTimerProvider } from './contexts/InterviewTimerContext'
import { NetworkQualityProvider } from './contexts/NetworkQualityContext'
import Header from './components/GenericLayout/Header'
import ProgressBar from './components/common-layout-components/ProgressBar'
import AIProfile from './components/common-layout-components/AIProfile'
import Conversation from './components/common-layout-components/Conversation'
import MicControls from './components/common-layout-components/MicControls'
import WebCam from './components/common-layout-components/WebCam'
import type { IMessage } from './types'
import { useInterviewSession } from './hooks/useInterviewSession'
import { useTTSStream } from './hooks/useTTSStream'
import { useVoiceInteraction } from './hooks/useVoiceInteraction'
import { useInterviewClock } from './hooks/useInterviewClock'
import CodeSnippet from './components/tool-layouts/CodeSnippet'
import CodeExercise from './components/tool-layouts/CodeExercise'
import type { SSEMetadataData, SSEToolCallData } from '@/types/sse.types'
import { navigateTo } from '@/router'
import useRecorder from '@/modules/recorder/hooks/useRecorder'
import MobileCameraOverlay, { type MobileCameraDetectionSnapshot } from '@/components/interview/MobileCameraOverlay'
import useModelsforHandsDetections from '@/hooks/useModelsForHandsDetections'
import MobileDeviceStatus from './components/common-layout-components/MobileDeviceStatus'
import type { PeerStatus } from '@/types/peerConnection'
import type { PeerMessage } from '@/types/peerDetection'
import { useMobilePeerConnection } from '@/context/MobilePeerConnectionContext'
import { Toaster } from 'sonner'
import { useFullscreenEnforcer } from '@/interview-engine/hooks/useFullscreenEnforcer'

interface RoleInterviewProps {
  interview_id?: string;
  initialInterviewStatus?: InterviewStatus;
  interviewData?: InterviewData;
  systemPromptStatus?: 'preparing' | 'completed';
  preparedInSession?: boolean;
  resumeFromTimestamp?: string;
  screenStream?: MediaStream | null;
  mobileCameraStream?: MediaStream | null;
  mobilePeerStatus?: PeerStatus;
  isMobilePeerConnected?: boolean;
  mobilePeerError?: string | null;
  mobileStreamKey?: string;
  isMobileWebcamRequired?: boolean;
}

const RoleInterview: React.FC<RoleInterviewProps> = ({
  interview_id,
  initialInterviewStatus,
  interviewData,
  systemPromptStatus,
  preparedInSession,
  resumeFromTimestamp,
  screenStream,
  mobileCameraStream,
  mobilePeerStatus,
  isMobilePeerConnected,
  mobilePeerError,
  mobileStreamKey,
  isMobileWebcamRequired = true,
}) => {
  const { sendPeerMessage } = useMobilePeerConnection();
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [streamingText, setStreamingText] = useState<string>('');
  const [activeTool, setActiveTool] = useState<{
    toolName: string;
    payload: Record<string, unknown>;
  } | null>(null);
  const [exerciseValue, setExerciseValue] = useState('');
  const [exerciseInitialValue, setExerciseInitialValue] = useState('');
  const [exerciseSubmitting, setExerciseSubmitting] = useState(false);
  const [exerciseMicEnabled, setExerciseMicEnabled] = useState(false);
  const [exerciseQuestionMode, setExerciseQuestionMode] = useState(false);
  const [exerciseQuestionUsed, setExerciseQuestionUsed] = useState(false);
  const [exerciseLocked, setExerciseLocked] = useState(false);
  const messageIdRef = useRef<number>(1);
  const timeLabelRef = useRef<string>('00:00:00');
  const recorderStartedRef = useRef(false);

  const {
    adoptExistingScreenStream,
    startRecording,
    stopRecording,
    stopAndMarkComplete,
    attachAudioElement,
  } = useRecorder({
    recorderId: interview_id,
    kind: 'interview',
    chunkDurationMs: 10_000,
  });

  const [pendingInterviewEndId, setPendingInterviewEndId] = useState<string | null>(null);
  const [ttsStartedAfterEnd, setTtsStartedAfterEnd] = useState(false);
  const isEndingInterviewRef = useRef(false);
  const [isInterviewEnding, setIsInterviewEnding] = useState(false);
  const hasNotifiedMobileInterviewEndRef = useRef(false);
  const mobileStream = useMemo(() => mobileCameraStream ?? null, [mobileCameraStream]);
  const latestMobileStreamKeyfromRemoteStream = useMemo(() => {
    if (mobileStreamKey) {
      return mobileStreamKey;
    }
    if (!mobileStream) {
      return "no-mobile-stream";
    }
    const trackIds = mobileStream.getTracks().map((track) => track.id).join("-");
    return `${mobileStream.id}-${trackIds}`;
  }, [mobileStream, mobileStreamKey]);

  const detectionModels = useModelsforHandsDetections();
  const [mobileDetectionSnapshot, setMobileDetectionSnapshot] = useState<MobileCameraDetectionSnapshot | null>(null);
  const handleMobileDetection = useCallback((snapshot: MobileCameraDetectionSnapshot) => {
    setMobileDetectionSnapshot(snapshot);
  }, []);
  const handsDetected = mobileDetectionSnapshot?.handsDetected ?? 0;
  const workspaceDetected = mobileDetectionSnapshot?.workspaceDetected ?? false;

  const { handleChunk: handleTtsChunk, flush: flushTts, isSpeaking } = useTTSStream({
    onAudioElementReady: attachAudioElement,
  });

  // Track if TTS started AFTER interview end was requested
  useEffect(() => {
    if (pendingInterviewEndId && isSpeaking && !ttsStartedAfterEnd) {
      setTtsStartedAfterEnd(true);
    }
  }, [pendingInterviewEndId, isSpeaking, ttsStartedAfterEnd]);

  useEffect(() => {
    if (!pendingInterviewEndId) return;
    if (hasNotifiedMobileInterviewEndRef.current) return;
    const payload: PeerMessage = {
      type: "interview-complete",
      payload: {
        interviewId: pendingInterviewEndId,
        reason: "interview-ended",
        timestamp: Date.now(),
      },
    };
    const sent = sendPeerMessage(payload);
    if (sent) hasNotifiedMobileInterviewEndRef.current = true;
  }, [pendingInterviewEndId, sendPeerMessage]);

  // End interview when: end requested AND TTS started after that AND TTS finished
  useEffect(() => {
    if (!pendingInterviewEndId) return;
    if (!ttsStartedAfterEnd) return; // Wait for final TTS to start
    if (isSpeaking) return; // Wait for final TTS to finish
    if (isEndingInterviewRef.current) return;

    isEndingInterviewRef.current = true;

    void stopAndMarkComplete().finally(() => {
      navigateTo(`/interview-ended/${encodeURIComponent(pendingInterviewEndId)}`);
    });
  }, [pendingInterviewEndId, ttsStartedAfterEnd, isSpeaking, stopAndMarkComplete]);

  const interviewDurationSeconds = interviewData?.duration ? interviewData.duration * 60 : undefined;

  const { timeLabel, totalSeconds, progressPercentage, timestampHHMMSS } = useInterviewClock({
    interviewId: interview_id,
    durationSeconds: interviewDurationSeconds,
    initialTimestamp: resumeFromTimestamp,
  });
  timeLabelRef.current = timeLabel;

  const onFirstChunkRef = useRef<() => void>(() => {});

  const addUserMessage = useCallback(
    (message: string, displayTime: string) => {
      const id = messageIdRef.current++;
      setMessages((prev) => [
        ...prev,
        {
          id,
          role: 'USER',
          message,
          time: displayTime,
        },
      ]);
    },
    [],
  );

  const handleToolCall = useCallback((data: SSEToolCallData) => {
    if (activeTool) return; // keep first active tool until hidden
    const args = (data.args as Record<string, unknown>) || {};
    setActiveTool({
      toolName: data.toolName,
      payload: args,
    });
  }, [activeTool]);

  const handleMetadata = useCallback((data: SSEMetadataData) => {
    const interviewEnded = (data as any)?.interviewEnded;
    const interviewEndedId = typeof interviewEnded?.interview_id === 'string'
      ? interviewEnded.interview_id
      : undefined;
    if (interviewEndedId) {
      // Defer navigation until TTS finishes speaking - set pending ID
      // The effect watching pendingInterviewEndId and isSpeaking will handle the actual end
      setPendingInterviewEndId(interviewEndedId);
      setIsInterviewEnding(true);
      return;
    }
    const toolDisplay = (data as any)?.toolDisplay;
    if (toolDisplay?.action === 'hide') {
      setActiveTool(null);
      setExerciseValue('');
      setExerciseInitialValue('');
      setExerciseSubmitting(false);
      setExerciseMicEnabled(false);
      setExerciseQuestionMode(false);
      setExerciseQuestionUsed(false);
      setExerciseLocked(false);
      return;
    }
    if (toolDisplay?.action === 'show') {
      setActiveTool({
        toolName: toolDisplay.toolName,
        payload: (toolDisplay.payload as Record<string, unknown>) || {},
      });
      if (toolDisplay.toolName === 'code_exercise') {
        const ex = (toolDisplay.payload as any)?.exercise;
        const starter = typeof ex?.code === 'string' ? ex.code : '';
        setExerciseInitialValue(starter);
        setExerciseValue(starter);
        setExerciseMicEnabled(false);
        setExerciseSubmitting(false);
        setExerciseQuestionMode(false);
        setExerciseQuestionUsed(false);
        setExerciseLocked(false);
      }
    }
  }, []);

  const { startInitialStream, isStreaming, sendUserMessage: sendUserMessageRaw } = useInterviewSession({
    interviewId: interview_id,
    initialInterviewStatus,
    candidateName: interviewData?.user_name,
    systemPromptStatus,
    preparedInSession,
    onChunk: (text) => {
      setStreamingText((prev) => prev + text);
      handleTtsChunk(text);
      onFirstChunkRef.current();
    },
    onComplete: (fullText) => {
      // Flush any remaining text in TTS buffer that wasn't a complete sentence
      flushTts();
      
      const trimmed = fullText.trim();
      if (!trimmed) {
        setStreamingText('');
        return;
      }
      const nextId = messageIdRef.current++;
      setMessages((prev) => [
        ...prev,
        {
          id: nextId,
          role: 'AI',
          message: trimmed,
          time: timeLabelRef.current,
        },
      ]);
      setStreamingText('');
    },
    onStreamError: (_msg) => {
      setStreamingText('');
    },
    onToolCall: handleToolCall,
    onMetadata: handleMetadata,
  });

  const sendUserMessage = useCallback(async (params: { message: string; timestamp: string }) => {
    if (exerciseQuestionMode && activeTool?.toolName === 'code_exercise') {
      setExerciseQuestionMode(false);
      setExerciseMicEnabled(false);
      setExerciseQuestionUsed(true);
      return sendUserMessageRaw({
        message: `CODE_EXERCISE_QUESTION:\n${params.message}`,
        timestamp: params.timestamp,
      });
    }
    return sendUserMessageRaw(params);
  }, [activeTool?.toolName, exerciseQuestionMode, sendUserMessageRaw]);

  const {
    micMode,
    aiInterviewState,
    analyzeText,
    liveTranscript,
    noticeText,
    handleDone,
    handleTtsSpeakingChange,
    onFirstChunk,
  } = useVoiceInteraction({
    interviewId: interview_id,
    timeLabel,
    timestampHHMMSS,
    sendUserMessage,
    onUserMessage: addUserMessage,
    isInterviewEnding,
  });

  const { containerRef } = useFullscreenEnforcer({
    enabled: Boolean(interview_id),
    warningText: 'During the interview you should be in the full screen mode.',
    reenterDelayMs: 800,
  });

  useEffect(() => {
    onFirstChunkRef.current = onFirstChunk;
  }, [onFirstChunk]);

  useEffect(() => {
    handleTtsSpeakingChange(isSpeaking);
  }, [handleTtsSpeakingChange, isSpeaking]);

  useEffect(() => {
    if (activeTool?.toolName === 'code_exercise' && isSpeaking) {
      setExerciseMicEnabled(false);
      setExerciseQuestionMode(false);
    }
  }, [activeTool?.toolName, isSpeaking]);

  useEffect(() => {
    if (activeTool?.toolName === 'code_exercise' && exerciseLocked && !isSpeaking && !exerciseQuestionMode) {
      setExerciseMicEnabled(true);
    }
  }, [activeTool?.toolName, exerciseLocked, isSpeaking, exerciseQuestionMode]);

  useEffect(() => {
    if (!interview_id) return;
    startInitialStream();
  }, [interview_id, startInitialStream]);

  useEffect(() => {
    if (activeTool?.toolName === 'code_exercise') {
      const ex = (activeTool.payload as any)?.exercise;
      const starter = typeof ex?.code === 'string' ? ex.code : '';
      setExerciseInitialValue(starter);
      setExerciseValue(starter);
      setExerciseMicEnabled(false);
      setExerciseSubmitting(false);
      setExerciseQuestionMode(false);
      setExerciseQuestionUsed(false);
      setExerciseLocked(false);
    }
  }, [activeTool]);

  const handleExerciseSubmit = useCallback(async () => {
    if (!activeTool || activeTool.toolName !== 'code_exercise') return;
    const code = exerciseValue.trim();
    if (!code) return;
    setExerciseSubmitting(true);
    try {
      await sendUserMessage({
        message: `CODE EXERCISE SUBMISSION (${(activeTool.payload as any)?.exercise?.language || 'unspecified language'}):\n${code}`,
        timestamp: timeLabelRef.current,
      });
      setExerciseLocked(true);
      setExerciseMicEnabled(true);
      setExerciseQuestionMode(false);
      setExerciseQuestionUsed(true);
    } finally {
      setExerciseSubmitting(false);
      setExerciseMicEnabled(true);
    }
  }, [activeTool, exerciseValue, sendUserMessage]);

  const handleExerciseQuestion = useCallback(() => {
    setExerciseMicEnabled(true);
    setExerciseQuestionMode(true);
  }, []);

  useEffect(() => {
    if (!interview_id || !screenStream || recorderStartedRef.current) {
      return;
    }

    // Mark as started before async work
    recorderStartedRef.current = true;

    const adoptAndStart = async () => {
      try {
        if (!screenStream.active || screenStream.getTracks().every((t) => t.readyState === 'ended')) {
          recorderStartedRef.current = false;
          return;
        }
        await adoptExistingScreenStream(screenStream);
        startRecording(interview_id);
      } catch (err) {
        recorderStartedRef.current = false;
      }
    };
    void adoptAndStart();
  }, [adoptExistingScreenStream, interview_id, screenStream, startRecording]);

  useEffect(() => {
    return () => {
      recorderStartedRef.current = false;
      void stopRecording();
    };
  }, [stopRecording]);

  return (
    <InterviewTimerProvider time={timeLabel} totalSeconds={totalSeconds} progressPercentage={progressPercentage}>
      <NetworkQualityProvider>
        <div ref={containerRef} className="w-full h-full overflow-hidden bg-[#FCFCFC] p-4"> {/* Added basic padding and bg matching reference */}
          <Toaster position="bottom-right" richColors />
          <div className="flex flex-col mx-auto h-full min-h-0 max-w-[98%]">
            <div className='flex flex-col gap-y-3 pb-4 md:pb-2 lg:pb-2.5 xl:pb-3 sm:gap-y-3 md:gap-y-1.5 lg:gap-y-3 xl:gap-y-3'>
              <Header
                title='Technical Interview'
              />
              <ProgressBar />
            </div>

            <div className="main-container w-full flex gap-8 overflow-hidden flex-1 min-h-0 md:gap-2 lg:gap-2.5 xl:gap-4 pb-2">
              {/* Left Column: AI Profile, Conversation/Tools, Mic Controls */}
              <div className="max-w-[45%] flex flex-col overflow-hidden flex-1 min-h-0 min-w-0">
                <div className="flex flex-1 min-h-0 flex-col gap-5 md:gap-2 lg:gap-2.5 xl:gap-4">
                  <div className="flex-2 min-h-0 flex">
                    <AIProfile interviewState={aiInterviewState} isAiRetrying={false} />
                  </div>
                  <div className="flex-[5.5] min-h-0 flex min-w-0 overflow-hidden">
                    <div className="flex-1 min-h-0 min-w-0">
                      {activeTool?.toolName === 'code_snippet' ? (() => {
                        const snippet = (activeTool.payload as any)?.codeSnippet;
                        if (!snippet || typeof snippet.code !== 'string' || typeof snippet.question !== 'string') {
                          return (
                            <Conversation
                              messages={messages}
                              responseText={streamingText}
                              isStreaming={isStreaming}
                              candidateName={interviewData?.user_name}
                            />
                          );
                        }
                        return (
                          <CodeSnippet
                            question={snippet.question}
                            codeSnippet={snippet.code}
                            language={typeof snippet.language === 'string' ? snippet.language : undefined}
                          />
                        );
                      })() : activeTool?.toolName === 'code_exercise' ? (() => {
                        const exercise = (activeTool.payload as any)?.exercise;
                        if (!exercise || typeof exercise.code !== 'string' || typeof exercise.question !== 'string') {
                          return (
                            <Conversation
                              messages={messages}
                              responseText={streamingText}
                              isStreaming={isStreaming}
                              candidateName={interviewData?.user_name}
                            />
                          );
                        }
                        return (
                          <CodeExercise
                            language={typeof exercise.language === 'string' ? exercise.language : 'plaintext'}
                            value={exerciseValue}
                            initialValue={exerciseInitialValue}
                            onChange={setExerciseValue}
                            onSubmit={handleExerciseSubmit}
                            submitting={exerciseSubmitting}
                            showHaveQuestions={!exerciseQuestionUsed && !exerciseLocked}
                            onHaveQuestions={handleExerciseQuestion}
                            disabled={isSpeaking || exerciseLocked}
                            disabledReason="AI is speaking, please wait."
                            question={exercise.question}
                            hideSubmit={exerciseLocked}
                          />
                        );
                      })() : (
                        <Conversation
                          messages={messages}
                          responseText={streamingText}
                          isStreaming={isStreaming}
                          candidateName={interviewData?.user_name}
                        />
                      )}
                    </div>
                  </div>
                  {isMobileWebcamRequired && (
                    <div className="flex-[2.5] min-h-0 flex items-center justify-center rounded-xl border border-slate-200 bg-white">
                      <MicControls
                        mode={micMode}
                        analyzeText={analyzeText}
                        liveTranscript={liveTranscript}
                        onDone={handleDone}
                        noticeText={noticeText ?? undefined}
                        disabled={micMode === "ai_speaking" || (activeTool?.toolName === 'code_exercise' && !exerciseMicEnabled)}
                        showInlineTranscript
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column: webcams and mobile overlay (aligned with GenericLayout) */}
              <div className="max-w-[55%] flex flex-col overflow-hidden flex-1 min-h-0 min-w-0">
                <div className="flex flex-1 min-h-0 flex-col gap-5 md:gap-2 lg:gap-2.5 xl:gap-4">
                  <div className="flex-5 min-h-0 flex overflow-hidden">
                    <div className="flex-1 min-h-0 flex overflow-hidden rounded-xl bg-white p-3 shadow-sm">
                      <WebCam candidateName={interviewData?.user_name} />
                    </div>
                  </div>
                  {isMobileWebcamRequired ? (
                    <div className="flex-5 min-h-0 min-w-0 flex overflow-hidden">
                      <div className="flex flex-1 min-h-0 min-w-0 gap-4 rounded-xl bg-white p-0 shadow-sm overflow-hidden">
                        <div className="flex-3 min-h-0 min-w-0">
                          <div className="relative h-full w-full overflow-hidden rounded-lg bg-black">
                            {mobileStream ? (
                              <>
                                 <div className="absolute inset-0 overflow-hidden">
                               <div className="w-full h-[167%] -translate-y-[40%]">
                                <MobileCameraOverlay
                                  key={latestMobileStreamKeyfromRemoteStream}
                                  stream={mobileStream}
                                  handDetector={detectionModels.handDetector}
                                  objectDetector={detectionModels.objectDetector}
                                  detectionError={detectionModels.error}
                                  enableDetections={!detectionModels.hasError}
                                  flipHorizontal={false}
                                  containerClassName="h-full w-full"
                                  videoClassName="h-full w-full object-cover"
                                  onDetectionUpdate={handleMobileDetection}
                                  mode="interview"
                                />
                                    </div>
                          </div>
                                {detectionModels.isPreparing && (
                                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 text-white">
                                    <div className="mb-3 h-10 w-10 animate-spin rounded-full border-b-2 border-white" />
                                    <p className="text-sm">{detectionModels.statusMessage || "Preparing workspace checks…"}</p>
                                  </div>
                                )}
                                {detectionModels.error && (
                                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-900/60 px-4 text-center text-white">
                                    <p className="text-sm font-semibold">Unable to run detection</p>
                                    <p className="text-xs opacity-80">{detectionModels.error.message}</p>
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/80">
                                <div className="h-12 w-12 animate-spin rounded-full border-2 border-white/30 border-t-transparent" />
                                <p className="text-sm">Waiting for phone camera…</p>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex min-h-0 min-w-0 flex-2 flex-col gap-4 border border-slate-200 rounded-lg p-4">
                          <MobileDeviceStatus
                            mobileCameraStream={mobileStream}
                            workspaceDetected={workspaceDetected}
                            handsDetected={handsDetected > 0}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex-5 min-h-0 min-w-0 flex overflow-hidden">
                      <div className="flex flex-1 min-h-0 min-w-0 rounded-xl bg-white p-4 shadow-sm">
                        <MicControls
                          mode={micMode}
                          analyzeText={analyzeText}
                          liveTranscript={liveTranscript}
                          onDone={handleDone}
                          noticeText={noticeText ?? undefined}
                          disabled={micMode === "ai_speaking" || (activeTool?.toolName === 'code_exercise' && !exerciseMicEnabled)}
                          showInlineTranscript
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </NetworkQualityProvider>
    </InterviewTimerProvider>
  )
}

export default RoleInterview
